<div class="telegram-ingest">
  @if (auth(); as a) {
    <div class="auth-bar" [class.disconnected]="!a.connected">
      <mat-icon inline>{{ a.connected ? 'link' : 'link_off' }}</mat-icon>
      @if (!a.configured) {
        <span>Telegram API credentials are not set on this backend.</span>
      } @else if (a.connected) {
        <span>Connected as {{ a.phone }}</span>
        <button mat-button (click)="startLogin()" [disabled]="loginBusy()">Re-connect</button>
      } @else {
        <span>Not connected — sign in as {{ a.phone }} to pull from Telegram.</span>
        <button mat-raised-button color="primary" (click)="startLogin()" [disabled]="loginBusy()">
          {{ loginBusy() ? 'Sending code…' : 'Connect Telegram' }}
        </button>
      }
    </div>
  }

  @if (loginStep() === 'code' || loginStep() === 'password') {
    <div class="login-box">
      @if (loginStep() === 'code') {
        <label>
          Login code
          <input
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            [(ngModel)]="loginCode"
            placeholder="Code Telegram just sent"
          />
        </label>
      } @else {
        <p class="muted">
          This account has Two-Step Verification on. The code was accepted — enter the
          account password to finish.
        </p>
        <label>
          2FA password
          <input type="password" autocomplete="off" [(ngModel)]="loginPassword" />
        </label>
      }
      <div class="login-actions">
        <button
          mat-raised-button
          color="primary"
          [disabled]="loginBusy() || (loginStep() === 'code' ? !loginCode : !loginPassword)"
          (click)="completeLogin()"
        >
          {{ loginBusy() ? 'Signing in…' : 'Sign in' }}
        </button>
        <button mat-button (click)="cancelLogin()" [disabled]="loginBusy()">Cancel</button>
      </div>
      @if (loginError()) {
        <p class="error">{{ loginError() }}</p>
      }
    </div>
  }

  @if (loginStep() === 'done') {
    <div class="login-box session-box">
      <p><strong>Signed in.</strong> Paste this into the environment as
        <code>TELEGRAM_SESSION</code> and restart the backend:</p>
      <textarea readonly rows="4">{{ newSession() }}</textarea>
      <div class="login-actions">
        <button mat-raised-button (click)="copySession()">
          {{ sessionCopied() ? 'Copied' : 'Copy' }}
        </button>
        <button mat-button (click)="cancelLogin()">Done</button>
      </div>
      <p class="muted">
        Treat this like a password — it grants full access to that Telegram account. It is
        shown once and is not stored by this backend.
      </p>
    </div>
  }

  <div class="ingest-bar">
    <button
      mat-raised-button
      color="primary"
      [disabled]="ingesting()"
      (click)="ingest()"
      matTooltip="Reads this seller's posts from the last 7 days and proposes a listing for each"
    >
      <mat-icon>cloud_download</mat-icon>
      {{ ingesting() ? 'Ingesting…' : 'Ingest from Telegram' }}
    </button>

    @if (expiresAt()) {
      <span class="muted" matTooltip="Staged listings are transient and are not stored">
        Staged until {{ expiresAt() | date: 'short' }}
      </span>
    }
  </div>

  @if (ingesting()) {
    <mat-progress-bar mode="indeterminate"></mat-progress-bar>
    <p class="muted">
      Reading posts, downloading photos and asking DeepSeek to read each caption — this
      takes a few seconds per post.
    </p>
  }

  @if (error()) {
    <p class="error">{{ error() }}</p>
  }

  @if (result(); as r) {
    <p class="muted summary">
      {{ r.staged }} listing{{ r.staged === 1 ? '' : 's' }} from {{ r.posts }} post{{ r.posts === 1 ? '' : 's' }}
      @if (r.multiListingPosts) {
        · {{ r.multiListingPosts }} post{{ r.multiListingPosts === 1 ? '' : 's' }} sold more than one thing
      }
      @if (r.alreadyListed) {
        · <span matTooltip="Already in this vendor's catalogue, so not offered again">{{ r.alreadyListed }} already listed</span>
      }
      @if (r.skippedNotListing) { · {{ r.skippedNotListing }} not listings }
      @if (r.skippedNoMedia) { · {{ r.skippedNoMedia }} without usable media }
    </p>
    @for (f of r.failures; track f) {
      <p class="error">{{ f }}</p>
    }
  }

  @if (loading()) {
    <p class="muted">Loading…</p>
  }

  @if (!loading() && !staged().length) {
    <p class="muted">
      Nothing staged. Click <strong>Ingest from Telegram</strong> to read this seller's
      recent posts — nothing reaches the catalogue until you confirm a listing.
    </p>
  }

  <div class="cards">
    @for (listing of staged(); track listing.id) {
      <div class="card">
        <div class="card-media">
          @if (listing.imageDataUrl) {
            <img [src]="listing.imageDataUrl" [alt]="listing.productName || 'Telegram media'" />
          } @else {
            <div class="no-image"><mat-icon>image_not_supported</mat-icon></div>
          }
          @if (listing.siblings > 1) {
            <span
              class="badge"
              matTooltip="This Telegram post sold several things; they all share its photo, so check this one matches"
            >1 of {{ listing.siblings }} in this post</span>
          }
        </div>

        <div class="card-body">
          <h3>{{ listing.productName || '(no name found)' }}</h3>

          <div class="price-row">
            <strong>{{ listing.price | currency: 'SGD' : 'symbol-narrow' }}</strong>
            @if (listing.sourcePrice != null) {
              <span
                class="muted"
                matTooltip="Seller's Telegram price, before this vendor's markup"
              >
                was {{ listing.sourcePrice | currency: 'SGD' : 'symbol-narrow' }} ({{ markupLabel(listing) }})
              </span>
            }
          </div>

          <dl class="fields">
            <dt>Option</dt>
            <dd>
              {{ listing.optionName }} — {{ listing.optionPrice | currency: 'SGD' : 'symbol-narrow' }}
            </dd>
            <dt>Stock</dt>
            <dd>{{ listing.qty }}</dd>
            <dt>Size</dt>
            <dd>{{ sizeLabel(listing) }}</dd>
            <dt>Posted</dt>
            <dd>{{ listing.postedAt | date: 'short' }}</dd>
          </dl>

          @if (listing.description) {
            <p class="description">{{ listing.description }}</p>
          }

          <div class="category-row">
            <label>
              Category
              <select
                [ngModel]="listing.categoryId"
                [ngModelOptions]="{ standalone: true }"
                (ngModelChange)="onMainChange(listing, $event)"
              >
                <option [ngValue]="null">— select —</option>
                @for (c of mainCategories(); track c.id) {
                  <option [ngValue]="c.id">{{ c.category_name }}</option>
                }
              </select>
            </label>

            <label>
              Subcategory
              <select
                [ngModel]="listing.subcategoryId"
                [ngModelOptions]="{ standalone: true }"
                [disabled]="!state(listing.id).subs.length"
                (ngModelChange)="onSubChange(listing, $event)"
              >
                <option [ngValue]="null">— none —</option>
                @for (c of state(listing.id).subs; track c.id) {
                  <option [ngValue]="c.id">{{ c.category_name }}</option>
                }
              </select>
            </label>

            <label>
              Type
              <select
                [ngModel]="listing.subsubcategoryId"
                [ngModelOptions]="{ standalone: true }"
                [disabled]="!state(listing.id).leaves.length"
                (ngModelChange)="onLeafChange(listing, $event)"
              >
                <option [ngValue]="null">— none —</option>
                @for (c of state(listing.id).leaves; track c.id) {
                  <option [ngValue]="c.id">{{ c.category_name }}</option>
                }
              </select>
            </label>
          </div>

          <p class="muted category-path">
            {{ listing.categoryPath }}
            @if (listing.categoryEdited) {
              <span matTooltip="You set this; a re-ingest will leave it alone">· edited</span>
            } @else {
              <span matTooltip="DeepSeek's guess — check it before confirming">· suggested</span>
            }
            @if (state(listing.id).saving) { · saving… }
          </p>

          @if (state(listing.id).error) {
            <p class="error">{{ state(listing.id).error }}</p>
          }

          <div class="actions">
            <button
              mat-raised-button
              color="primary"
              [disabled]="state(listing.id).confirming || !canConfirm(listing)"
              [matTooltip]="canConfirm(listing)
                ? 'Creates the product, its price, option and images for this vendor'
                : 'Needs a name, a price and a category first'"
              (click)="confirm(listing)"
            >
              {{ state(listing.id).confirming ? 'Confirming…' : 'Confirm' }}
            </button>

            @if (listing.caption) {
              <details class="caption">
                <summary>Original post</summary>
                <pre>{{ listing.caption }}</pre>
              </details>
            }
          </div>
        </div>
      </div>
    }
  </div>
</div>
